// SmoothAircraftMarkerMapbox.jsx - Great-circle smooth plane animation for Mapbox
// Uses progress-based interpolation along great-circle path at 60fps

import { useEffect, useRef, useState } from 'react';
import planeIcon from '../../assets/plane-icon.png';
import b2StealthIcon from '../../assets/b2-stealth.svg';
import { intermediatePoint, initialBearing } from '../sim/GreatCircleNav.jsx';
import { debug } from '../../lib/debug.js';

export function SmoothAircraftMarkerMapbox({
  map,
  originLat,
  originLon,
  destLat,
  destLon,
  flightDurationMs,
  gameSpeed = 1,
  isPaused = false,
  onFollow,
  onArrived,
  onPositionUpdate,
  flight,
  isFollowing = false,
}) {
  // DEBUG: Validate coordinates - detect lat/lon swap or invalid values
  if (flight && (originLat < -90 || originLat > 90 || destLat < -90 || destLat > 90)) {
    console.error(`[SmoothAircraftMarker] INVALID LATITUDE for ${flight.flightNumber}: originLat=${originLat}, destLat=${destLat}`);
  }
  if (flight && (originLon < -180 || originLon > 180 || destLon < -180 || destLon > 180)) {
    console.error(`[SmoothAircraftMarker] INVALID LONGITUDE for ${flight.flightNumber}: originLon=${originLon}, destLon=${destLon}`);
  }
  // Check for possible lat/lon swap (US West Coast should have negative longitude)
  if (flight && originLon > 0 && originLon < 90 && originLat < -100) {
    console.error(`[SmoothAircraftMarker] POSSIBLE LAT/LON SWAP for ${flight.flightNumber}: origin=[${originLat}, ${originLon}] looks reversed!`);
  }
  const divRef = useRef(null);
  const popupRef = useRef(null);
  const ballRef = useRef(null);
  const rafRef = useRef(null);
  const speedRef = useRef(gameSpeed);
  const pausedRef = useRef(isPaused);
  const followingRef = useRef(isFollowing);
  const arrivedRef = useRef(false);
  const positionUpdateCounter = useRef(0);
  const [isHovering, setIsHovering] = useState(false);

  const onArrivedRef = useRef(onArrived);
  const onPositionUpdateRef = useRef(onPositionUpdate);
  const flightRef = useRef(flight);

  useEffect(() => { speedRef.current = gameSpeed; }, [gameSpeed]);
  useEffect(() => { pausedRef.current = isPaused; }, [isPaused]);
  useEffect(() => { onArrivedRef.current = onArrived; }, [onArrived]);
  useEffect(() => { onPositionUpdateRef.current = onPositionUpdate; }, [onPositionUpdate]);
  useEffect(() => { flightRef.current = flight; }, [flight]);
  useEffect(() => {
    followingRef.current = isFollowing;
    if (!isFollowing && ballRef.current) {
      ballRef.current.followReady = false;
    }
  }, [isFollowing]);

  useEffect(() => {
    if (!map || !divRef.current) return;

    const isNewFlight = !ballRef.current || ballRef.current.flightId !== flight?.id;

    if (isNewFlight) {
      arrivedRef.current = false;
      const startProgress = flight?.distanceFraction ?? 0;
      const startPos = intermediatePoint(originLat, originLon, destLat, destLon, startProgress);
      const bearing = initialBearing(startPos.lat, startPos.lon, destLat, destLon);

      // DEBUG: Log marker initialization
      debug(`[MARKER INIT] New flight marker:`, {
        flightId: flight?.id,
        flightNumber: flight?.flightNumber,
        flightOrigin: flight?.origin,
        flightDestination: flight?.destination,
        propsOriginLat: originLat,
        propsOriginLon: originLon,
        propsDestLat: destLat,
        propsDestLon: destLon,
        startProgress,
        startLat: startPos.lat,
        startLon: startPos.lon,
        previousFlightId: ballRef.current?.flightId || 'NONE',
      });

      ballRef.current = {
        flightId: flight?.id,
        progress: startProgress,
        lat: startPos.lat,
        lon: startPos.lon,
        bearing,
        lastT: performance.now(),
      };
      divRef.current.style.display = 'block';
    }

    const loop = () => {
      const b = ballRef.current;
      if (!b || !divRef.current) return;

      if (arrivedRef.current) {
        divRef.current.style.display = 'none';
        return;
      }

      const now = performance.now();
      const dt = Math.min(now - b.lastT, 100); // Cap dt to 100ms to prevent jumps on click/lag
      b.lastT = now;

      // SYNC FIX: If game says flight is at destination, sync marker immediately
      // This prevents desync when game speed outpaces marker animation
      // For pending arrivals (_addedAt exists), always target 1.0 since data is frozen
      const isPendingForSync = !!flightRef.current?._addedAt;
      const gameProgress = isPendingForSync ? 1.0 : (flightRef.current?.distanceFraction ?? 0);
      if (gameProgress >= 1.0 && b.progress < 1.0) {
        b.progress = 1.0;
      }

      if (!pausedRef.current) {
        // SYNC FIX: Use game's distanceFraction as source of truth
        // Previously calculated own progress which got out of sync with GameLoopEngine
        // GameLoopEngine uses baseFlightScale (8x) that marker wasn't accounting for
        // BUT: For pending arrivals (_addedAt exists), the data is FROZEN - don't sync to stale data!
        const isPendingArrival = !!flightRef.current?._addedAt;
        const targetProgress = isPendingArrival
          ? 1.0  // Pending arrivals should always progress to destination
          : (flightRef.current?.distanceFraction ?? b.progress);

        // Smoothly interpolate toward game state (prevents jerky jumps)
        const smoothingFactor = 0.15; // Higher = more responsive, lower = smoother
        b.progress += (targetProgress - b.progress) * smoothingFactor;
        b.progress = Math.min(1, Math.max(0, b.progress));

        const pos = intermediatePoint(originLat, originLon, destLat, destLon, b.progress);
        b.lat = pos.lat;
        b.lon = pos.lon;

        if (b.progress < 0.99) {
          b.bearing = initialBearing(b.lat, b.lon, destLat, destLon);
        }
      }

      if (b.progress >= 1.0) {
        b.lat = destLat;
        b.lon = destLon;
        const pt = map.project([b.lon, b.lat]);
        divRef.current.style.transform = "translate(" + (pt.x - 21) + "px, " + (pt.y - 21) + "px) rotate(" + b.bearing + "deg)";
        if (popupRef.current) {
          popupRef.current.style.transform = "translate(" + (pt.x + 25) + "px, " + (pt.y - 60) + "px)";
        }
        if (!arrivedRef.current) {
          arrivedRef.current = true;
          setTimeout(() => {
            if (divRef.current) divRef.current.style.display = 'none';
            onArrivedRef.current && onArrivedRef.current(flightRef.current);
          }, 3000);
        }
        return;
      }

      const pt = map.project([b.lon, b.lat]);
      divRef.current.style.transform = "translate(" + (pt.x - 21) + "px, " + (pt.y - 21) + "px) rotate(" + b.bearing + "deg)";
      if (popupRef.current) {
        popupRef.current.style.transform = "translate(" + (pt.x + 25) + "px, " + (pt.y - 60) + "px)";
      }

      if (followingRef.current && b.followReady) {
        map.setCenter([b.lon, b.lat]);
      }

      positionUpdateCounter.current++;
      if (onPositionUpdateRef.current && flight && positionUpdateCounter.current % 5 === 0) {
        onPositionUpdateRef.current(flight.id, b.lat, b.lon);
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    const sync = () => {
      const b = ballRef.current;
      if (b && divRef.current && !arrivedRef.current) {
        const pt = map.project([b.lon, b.lat]);
        divRef.current.style.transform = "translate(" + (pt.x - 21) + "px, " + (pt.y - 21) + "px) rotate(" + b.bearing + "deg)";
        if (popupRef.current) {
          popupRef.current.style.transform = "translate(" + (pt.x + 25) + "px, " + (pt.y - 60) + "px)";
        }
      }
    };
    // Use 'render' event to sync position with map's render cycle - prevents jitter during zoom
    map.on('render', sync);

    return () => {
      cancelAnimationFrame(rafRef.current);
      map.off('render', sync);
    };
  }, [map, originLat, originLon, destLat, destLon, flightDurationMs, flight?.id]);

  const showPopup = isHovering || isFollowing;

  return (
    <>
      <div
        ref={divRef}
        data-flight-id={flight?.id}
        onClick={(e) => {
          e.stopPropagation();
          // Use ballRef position if available, otherwise fallback to flight's current position
          const lon = ballRef.current?.lon ?? flight?.currentLon;
          const lat = ballRef.current?.lat ?? flight?.currentLat;

          if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
            debug('[SmoothAircraftMarker] Click failed - no valid position for', flight?.flightNumber);
            return;
          }

          map.easeTo({
            center: [lon, lat],
            zoom: 9,
            duration: 1200,
            easing: (t) => t * (2 - t) // Smooth ease-out curve
          });
          onFollow && onFollow(flight);
          map.once('moveend', () => { if (ballRef.current) ballRef.current.followReady = true; });
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{
          position: 'absolute', left: 0, top: 0, width: 42, height: 42,
          cursor: 'pointer', zIndex: 1000, willChange: 'transform',
          display: 'none', pointerEvents: 'auto',
        }}
      >
        <img src={flight?.eosId === 'blackout' ? b2StealthIcon : planeIcon} alt="aircraft" style={{
          width: 42, height: 42,
          // Anomalous visual effects
          filter: flight?.eosId === 'blackout'
            // Blackout: Dark stealth appearance for B2 silhouette
            ? 'brightness(0.2) drop-shadow(0 0 4px rgba(90, 0, 128, 0.8))'
            : flight?.eosId === 'ghost-apotheosis'
            // Ghost Apotheosis: Purple plane with mythic glow
            ? 'invert(25%) sepia(100%) saturate(500%) hue-rotate(260deg) drop-shadow(0 0 15px rgba(180, 80, 255, 1)) drop-shadow(0 0 30px rgba(150, 50, 255, 0.9)) drop-shadow(0 0 45px rgba(120, 0, 255, 0.6))'
            : flight?.noWindows
            // Blackout: Dark purple tint for no-windows flight
            ? 'brightness(0.3) sepia(1) saturate(3) hue-rotate(230deg) drop-shadow(2px 0 0 #5a0080) drop-shadow(-2px 0 0 #5a0080) drop-shadow(0 2px 0 #5a0080) drop-shadow(0 -2px 0 #5a0080)'
            : flight?.eosId === 'ghost-freight' || flight?.phantomCargo
            // Ghost Freight: Blurry white spectral ghost appearance
            ? 'brightness(0) invert(1) blur(1.5px) drop-shadow(0 0 8px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 16px rgba(255, 255, 255, 0.5))'
            : flight?.quantumState
            // Schrodinger Jet: Glitchy quantum superposition with red/cyan chromatic aberration
            ? 'brightness(1) drop-shadow(-3px 0 0 rgba(255, 0, 0, 0.7)) drop-shadow(3px 0 0 rgba(0, 255, 255, 0.7))'
            : 'brightness(0) invert(1) drop-shadow(2px 0 0 #000) drop-shadow(-2px 0 0 #000) drop-shadow(0 2px 0 #000) drop-shadow(0 -2px 0 #000)',
          // Ghost Freight: Semi-transparent
          opacity: (flight?.eosId === 'ghost-freight' || flight?.phantomCargo) ? 0.6 : 1,
          // VTOnly: Add bouncing animation for vertical-only flights
          animation: flight?.animation === 'vertical' ? 'vtonly-bounce 0.5s ease-in-out infinite'
            : flight?.phantomCargo ? 'ghost-flicker 2s ease-in-out infinite'
            : flight?.quantumState ? 'quantum-glitch 0.3s ease-in-out infinite' : undefined,
        }} draggable={false} />
        {/* CSS for anomalous animations */}
        {(flight?.animation === 'vertical' || flight?.phantomCargo || flight?.quantumState) && (
          <style>{`
            @keyframes vtonly-bounce {
              0%, 100% { transform: translateY(0) scale(1); }
              50% { transform: translateY(-15px) scale(1.1); }
            }
            @keyframes ghost-flicker {
              0%, 100% { opacity: 0.6; }
              25% { opacity: 0.3; }
              50% { opacity: 0.7; }
              75% { opacity: 0.4; }
            }
            @keyframes quantum-glitch {
              0%, 100% { transform: translateX(0); }
              20% { transform: translateX(-2px) skewX(1deg); }
              40% { transform: translateX(2px) skewX(-1deg); }
              60% { transform: translateX(-1px); }
              80% { transform: translateX(1px) skewX(0.5deg); }
            }
          `}</style>
        )}
      </div>
      {showPopup && flight && (
        <div ref={popupRef} style={{
          position: 'absolute', left: 0, top: 0,
          background: 'rgba(0, 0, 0, 0.85)', color: '#ffffff',
          padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
          fontFamily: 'monospace', lineHeight: '1.4',
          boxShadow: '0 2px 8px rgba(0,0,0,0.5)', pointerEvents: 'none',
          whiteSpace: 'nowrap', zIndex: 1001, willChange: 'transform',
        }}>
          <div style={{ fontWeight: 'bold' }}>{flight.origin} -&gt; {flight.destination}</div>
          {/* IMPORTANT: Show flightNumber first, not registration - DO NOT CHANGE */}
          <div style={{ fontSize: '11px', color: '#a0a0a0' }}>{flight.flightNumber || flight.aircraftRegistration || 'N/A'}</div>
          <div style={{ fontSize: '11px', color: '#a0a0a0' }}>{flight.aircraftType || 'Unknown'}</div>
          {(flight.groundSpeed || flight.altitude) && (
            <div style={{ fontSize: '11px', color: '#60ffd0', display: 'flex', gap: '8px' }}>
              {flight.groundSpeed && <span>{flight.groundSpeed} kts</span>}
              {flight.altitude > 18000 ? <span>FL{Math.round(flight.altitude / 100)}</span> : flight.altitude > 0 && <span>{flight.altitude.toLocaleString()} ft</span>}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default SmoothAircraftMarkerMapbox;
